import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiHandle from "services/apiHandle";

const initialState = {
  bookings: [],
  currentBooking: null,
  loading: false,
  error: null,
  message: "",
  showMessage: false,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
    hasNextPage: false,
    hasPrevPage: false,
  },
  // Booking flow states
  bookingFlow: {
    selectedVehicle: null,
    selectedProvider: null,
    selectedServices: [],
    selectedDate: null,
    selectedTimeSlots: {},
    additionalNotes: "",
  },
  // Available slots
  availableSlots: [],
  slotsLoading: false,
  slotsError: null,
  // Create booking
  createBookingLoading: false,
  createBookingError: null,
  // Booking stats (from /bookings/stats/overview)
  bookingStats: null,
  bookingStatsLoading: false,
};

// Fetch all bookings for provider
export const fetchProviderBookings = createAsyncThunk(
  "bookings/fetchProviderBookings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiHandle("/bookings/provider/all", "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch bookings");
    }
  },
);

// Fetch all bookings for owner
export const fetchOwnerBookings = createAsyncThunk(
  "bookings/fetchOwnerBookings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiHandle("/bookings/owner/all", "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch bookings");
    }
  },
);

// Fetch booking stats (total bookings, earnings, etc.)
export const fetchBookingStats = createAsyncThunk(
  "bookings/fetchBookingStats",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiHandle("/bookings/stats/overview", "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch booking stats");
    }
  },
);

// Fetch booking by ID (provider)
export const fetchBookingById = createAsyncThunk(
  "bookings/fetchBookingById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiHandle(`/bookings/provider/${id}`, "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch booking details");
    }
  },
);

// Fetch booking by ID for owner
export const fetchOwnerBookingById = createAsyncThunk(
  "bookings/fetchOwnerBookingById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiHandle(`/bookings/owner/${id}`, "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch booking details");
    }
  },
);

// Toggle booking status (accept/cancel)
export const toggleBookingStatus = createAsyncThunk(
  "bookings/toggleBookingStatus",
  async ({ id, action }, { rejectWithValue }) => {
    try {
      const res = await apiHandle(
        `/bookings/provider/toggle-status/${id}`,
        "POST",
        { action },
      );
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update booking status");
    }
  },
);

// Owner complete or cancel booking
export const ownerCompleteBooking = createAsyncThunk(
  "bookings/ownerCompleteBooking",
  async ({ id, action }, { rejectWithValue }) => {
    try {
      const res = await apiHandle(
        `/bookings/owner-complete/${id}`,
        "PATCH",
        { action },
      );
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update booking");
    }
  },
);

// Submit booking review
export const submitBookingReview = createAsyncThunk(
  "bookings/submitBookingReview",
  async ({ id, rating, review }, { rejectWithValue }) => {
    try {
      const res = await apiHandle(
        `/bookings/${id}/review`,
        "POST",
        { rating, review },
      );
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to submit review");
    }
  },
);

// ── Missing from portal but present in app ──────────────────────

// Create booking (matches app's CREATE_BOOKING)
export const createBooking = createAsyncThunk(
  "bookings/createBooking",
  async (bookingPayload, { rejectWithValue }) => {
    try {
      const res = await apiHandle("/bookings/create", "POST", bookingPayload);
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create booking");
    }
  },
);

// Fetch available slots (matches app's GET_AVAILABLE_SLOTS)
export const fetchAvailableSlots = createAsyncThunk(
  "bookings/fetchAvailableSlots",
  async (body, { rejectWithValue }) => {
    try {
      const res = await apiHandle("/bookings/available-slots", "POST", body);
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch available slots");
    }
  },
);

// Review additional service (matches app's REVIEW_ADDITIONAL_SERVICE)
export const reviewAdditionalService = createAsyncThunk(
  "bookings/reviewAdditionalService",
  async ({ bookingId, serviceIndex, body }, { rejectWithValue }) => {
    try {
      const res = await apiHandle(
        `/bookings/additional-services/${bookingId}/${serviceIndex}/review`,
        "PATCH",
        body,
      );
      return res;
    } catch (err) {
      return rejectWithValue(
        err?.message || "Failed to review additional service",
      );
    }
  },
);

export const bookingsSlice = createSlice({
  name: "bookings",
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.message = "";
      state.showMessage = false;
      state.error = null;
    },
    setCurrentBooking: (state, action) => {
      state.currentBooking = action.payload;
    },
    // Booking flow actions
    setSelectedVehicle: (state, action) => {
      state.bookingFlow.selectedVehicle = action.payload;
    },
    setSelectedProvider: (state, action) => {
      state.bookingFlow.selectedProvider = action.payload;
    },
    setSelectedServices: (state, action) => {
      state.bookingFlow.selectedServices = action.payload;
    },
    setBookingDate: (state, action) => {
      state.bookingFlow.selectedDate = action.payload;
    },
    setBookingTimeSlots: (state, action) => {
      state.bookingFlow.selectedTimeSlots = action.payload;
    },
    setAdditionalNotes: (state, action) => {
      state.bookingFlow.additionalNotes = action.payload;
    },
    clearBookingFlow: (state) => {
      state.bookingFlow = {
        selectedVehicle: null,
        selectedProvider: null,
        selectedServices: [],
        selectedDate: null,
        selectedTimeSlots: {},
        additionalNotes: "",
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch provider bookings
      .addCase(fetchProviderBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProviderBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload.data || [];
        state.pagination = action.payload.pagination || initialState.pagination;
        state.message = action.payload.message;
      })
      .addCase(fetchProviderBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.showMessage = true;
      })
      // Fetch booking stats
      .addCase(fetchBookingStats.pending, (state) => {
        state.bookingStatsLoading = true;
      })
      .addCase(fetchBookingStats.fulfilled, (state, action) => {
        state.bookingStatsLoading = false;
        state.bookingStats = action.payload.data || action.payload;
      })
      .addCase(fetchBookingStats.rejected, (state) => {
        state.bookingStatsLoading = false;
      })
      // Fetch owner bookings
      .addCase(fetchOwnerBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOwnerBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload.data || [];
        state.pagination = action.payload.pagination || initialState.pagination;
        state.message = action.payload.message;
      })
      .addCase(fetchOwnerBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.showMessage = true;
      })
      // Fetch booking by ID
      .addCase(fetchBookingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookingById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload.data;
        state.message = action.payload.message;
      })
      .addCase(fetchBookingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.showMessage = true;
      })
      // Fetch owner booking by ID
      .addCase(fetchOwnerBookingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOwnerBookingById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload.data;
        state.message = action.payload.message;
      })
      .addCase(fetchOwnerBookingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.showMessage = true;
      })
      // Toggle booking status
      .addCase(toggleBookingStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleBookingStatus.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentBooking) {
          state.currentBooking = action.payload.data;
        }
        const index = state.bookings.findIndex(
          (b) => b._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.bookings[index] = action.payload.data;
        }
        state.message = action.payload.message;
        state.showMessage = true;
      })
      .addCase(toggleBookingStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.showMessage = true;
      })
      // Owner complete/cancel booking
      .addCase(ownerCompleteBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(ownerCompleteBooking.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentBooking) {
          state.currentBooking = action.payload.data;
        }
        const index = state.bookings.findIndex(
          (b) => b._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.bookings[index] = action.payload.data;
        }
        state.message = action.payload.message;
        state.showMessage = true;
      })
      .addCase(ownerCompleteBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.showMessage = true;
      })
      // Submit booking review
      .addCase(submitBookingReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitBookingReview.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentBooking) {
          state.currentBooking = action.payload.data;
        }
        const index = state.bookings.findIndex(
          (b) => b._id === action.payload.data._id,
        );
        if (index !== -1) {
          state.bookings[index] = action.payload.data;
        }
        state.message = action.payload.message;
        state.showMessage = true;
      })
      .addCase(submitBookingReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.showMessage = true;
      })
      // Create booking
      .addCase(createBooking.pending, (state) => {
        state.createBookingLoading = true;
        state.createBookingError = null;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.createBookingLoading = false;
        state.message = action.payload.message || "Booking created successfully!";
        state.showMessage = true;
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.createBookingLoading = false;
        state.createBookingError = action.payload;
        state.error = action.payload;
        state.showMessage = true;
      })
      // Fetch available slots
      .addCase(fetchAvailableSlots.pending, (state) => {
        state.slotsLoading = true;
        state.slotsError = null;
      })
      .addCase(fetchAvailableSlots.fulfilled, (state, action) => {
        state.slotsLoading = false;
        state.availableSlots = action.payload.data || [];
      })
      .addCase(fetchAvailableSlots.rejected, (state, action) => {
        state.slotsLoading = false;
        state.slotsError = action.payload;
      })
      // Review additional service
      .addCase(reviewAdditionalService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reviewAdditionalService.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentBooking) {
          state.currentBooking = action.payload.data;
        }
        state.message = action.payload.message || "Additional service reviewed";
        state.showMessage = true;
      })
      .addCase(reviewAdditionalService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.showMessage = true;
      });
  },
});

export const {
  clearMessage,
  setCurrentBooking,
  setSelectedVehicle,
  setSelectedProvider,
  setSelectedServices,
  setBookingDate,
  setBookingTimeSlots,
  setAdditionalNotes,
  clearBookingFlow,
} = bookingsSlice.actions;

export default bookingsSlice.reducer;
